/**
 * Admin → Health.
 *
 * Compact dashboard of system-wide signals. Polls every 30s (configured on
 * the query). Each tile is a `StatTile`.
 */
import { adminApi } from "../../api/admin";
import { PageHeader } from "../../components/PageHeader";
import { QueryBoundary } from "../../components/QueryBoundary";
import { Skeleton } from "../../components/Skeleton";
import { StatTile } from "../../components/StatTile";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[unit]}`;
}

export function HealthPage() {
  const query = adminApi.health.get();

  const refreshedAt = query.dataUpdatedAt
    ? new Date(query.dataUpdatedAt).toLocaleTimeString()
    : null;

  return (
    <>
      <PageHeader
        title="Health"
        description="System-wide signals at a glance. Refreshes every 30 seconds."
        actions={
          <span className="text-xs text-ink-low">
            {query.isFetching ? "refreshing…" : refreshedAt ? `last refreshed ${refreshedAt}` : null}
          </span>
        }
      />

      <QueryBoundary
        query={query}
        loading={
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height={120} width="100%" />
            ))}
          </div>
        }
      >
        {(h) => (
          <section
            className="grid gap-4"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}
          >
            <StatTile
              label="Cron users"
              figure={<span className="tnum">{h.cron_users}</span>}
              delta="with scheduled jobs"
              deltaTone={h.cron_users > 0 ? "up" : null}
            />
            <StatTile
              label="Active sessions"
              figure={<span className="tnum">{h.active_sessions}</span>}
              delta="MCP connections"
              deltaTone={h.active_sessions > 0 ? "up" : null}
            />
            <StatTile
              label="Pending confirmations"
              figure={<span className="tnum">{h.pending_confirmations}</span>}
              delta="awaiting user"
              deltaTone={h.pending_confirmations > 0 ? "warn" : null}
            />
            <StatTile
              label="Audit (24h)"
              figure={<span className="tnum">{h.audit_last_24h}</span>}
              delta="tool calls"
            />
            <StatTile
              label="Denials (24h)"
              figure={<span className="tnum">{h.denials_last_24h}</span>}
              delta="auth rejections"
              deltaTone={h.denials_last_24h > 0 ? "warn" : null}
            />
            <StatTile
              label="LTM disk"
              figure={<span className="tnum">{formatBytes(h.ltm_disk_bytes)}</span>}
              delta="long-term memory"
            />
          </section>
        )}
      </QueryBoundary>
    </>
  );
}
